/**
 * L'acte exporté en mémoire : la source unique de toute lecture de forme du
 * canvas (spec #1858).
 *
 * `doc.export.toDocx()` rend le fichier Word de l'acte sans rien télécharger
 * ni enregistrer. Mesuré sur SuperDoc 2.15.1 (sonde `mesure-source-unique-
 * export`, 2026-10-01, et garde `canvas-lecture-de-forme`) : il ne change ni
 * l'historique d'annulation, ni les révisions en attente, ni le mode de
 * l'éditeur, ni la révision du document, et aucune frappe de l'avocat n'est
 * perdue pendant qu'il tourne. Son effet sur l'état « à enregistrer » du
 * brouillon n'a pas pu être mesuré au banc (spec #1858, « limites des mesures »).
 *
 * ⚠️ UN export par appel d'outil, jamais un par paragraphe : il coûte 10 à
 * 30 ms sur un petit acte, mais 0,4 à 1,2 s sur un acte de 1,5 Mo.
 */

import type { SuperDocDocumentApi } from './superDocDocumentApi';

/**
 * L'éditeur n'a pas rendu le fichier de l'acte. `code` dit si la surface manque
 * ou si l'appel a échoué ; le message est en anglais, il part tel quel dans le
 * reçu que lit le modèle.
 */
export class ExportDeLActeImpossible extends Error {
  constructor(
    readonly code: 'export_unsupported' | 'export_failed',
    message: string,
  ) {
    super(message);
    this.name = 'ExportDeLActeImpossible';
  }
}

function estEnregistrement(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}

/**
 * Exporte l'acte affiché en octets de fichier Word, révisions en attente
 * conservées telles quelles (mode par défaut de l'éditeur).
 *
 * @throws {ExportDeLActeImpossible} si l'éditeur n'expose pas l'export en
 *   mémoire, s'il le refuse, ou s'il ne rend pas d'octets.
 */
export async function exporterLActeEnMemoire(doc: SuperDocDocumentApi): Promise<Uint8Array> {
  const surface = doc.export;
  if (typeof surface?.toDocx !== 'function') {
    throw new ExportDeLActeImpossible(
      'export_unsupported',
      'the mounted editor does not expose editor.doc.export.toDocx, so the form of this document cannot be read. '
      + 'Nothing was modified. Form reads are unavailable on this document; other canvas_inspect reads still work.',
    );
  }
  let resultat: unknown;
  try {
    // Appelé SUR la surface, jamais détaché : `doc.export` rend une méthode
    // neuve à chaque lecture (mesuré 2026-10-02), liée à son éditeur.
    resultat = await surface.toDocx();
  } catch (erreur) {
    throw new ExportDeLActeImpossible(
      'export_failed',
      `the in-memory export of the document (editor.doc.export.toDocx) failed: ${erreur instanceof Error ? erreur.message : String(erreur)}. `
      + 'Nothing was modified. Run the same read again; if it fails again, the form of this document cannot be read.',
    );
  }
  const contenu = estEnregistrement(resultat) ? resultat.contentBase64 : undefined;
  if (typeof contenu !== 'string' || contenu.length === 0) {
    const echec = estEnregistrement(resultat) && estEnregistrement(resultat.failure)
      ? ` (${String(resultat.failure.code)}: ${String(resultat.failure.message)})`
      : '';
    throw new ExportDeLActeImpossible(
      'export_failed',
      `the in-memory export of the document (editor.doc.export.toDocx) returned no bytes${echec}. `
      + 'Nothing was modified. Run the same read again; if it fails again, the form of this document cannot be read.',
    );
  }
  const binaire = atob(contenu);
  const octets = new Uint8Array(binaire.length);
  for (let rang = 0; rang < binaire.length; rang += 1) octets[rang] = binaire.charCodeAt(rang);
  return octets;
}
